import { Link } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PaperCard } from "@/components/ui/paper-card";
import { Timestamp } from "@/components/ui/timestamp";
import { WalkthroughPersonaHeader } from "@/components/experience/walkthrough-persona-header";
import { DashboardSectionHeader } from "./dashboard-section-header";
import type { WalkthroughListItem } from "./dashboard-types";

interface WalkthroughHeroStatusCardProps {
  projectId: string;
  latest: WalkthroughListItem;
  lastCompleted?: WalkthroughListItem;
  scenarioTo: string | null;
}

export function ActiveWalkthroughHeroCard({
  projectId,
  latest,
  lastCompleted,
  scenarioTo,
}: WalkthroughHeroStatusCardProps) {
  return (
    <section>
      <DashboardSectionHeader
        kicker="Latest walkthrough"
        note="In progress"
        action={
          lastCompleted ? (
            <Link
              to={`/projects/${projectId}/walkthroughs/${lastCompleted.id}`}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              View last completed report →
            </Link>
          ) : undefined
        }
      />
      <PaperCard
        as="article"
        padding="md"
        className="relative overflow-hidden space-y-6 border-sky-500/30 bg-sky-500/[0.02]"
      >
        <WalkthroughPersonaHeader
          personaName={latest.personaName}
          personaTo={
            latest.personaId
              ? `/projects/${projectId}/personas/${latest.personaId}`
              : null
          }
          scenarioName={latest.scenarioName}
          scenarioTo={scenarioTo}
          timestamp={latest.createdAt}
        />

        <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 px-2.5 py-0.5 text-xs font-semibold text-sky-600 dark:text-sky-400">
              <Loader2 className="h-3 w-3 animate-spin text-sky-600 dark:text-sky-400" />
              {latest.status === "pending" ? "Queued" : "In progress"}
            </span>
            <span className="text-xs text-muted-foreground">
              Streaming steps and live observations
            </span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Persona walkthrough is currently active. Steps, browser screenshots,
            and thoughts are streaming in real-time.
          </p>
        </div>

        {lastCompleted && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/30 px-3.5 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span>Last successful run:</span>
              <strong className="font-medium text-foreground">
                {lastCompleted.scenarioName ?? "Scenario"}
              </strong>
              <span>·</span>
              <Timestamp
                date={lastCompleted.completedAt ?? lastCompleted.createdAt}
              />
            </div>
            <Link
              to={`/projects/${projectId}/walkthroughs/${lastCompleted.id}`}
              className="font-medium text-primary hover:underline"
            >
              View report →
            </Link>
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-t border-border/60 pt-4">
          <Button asChild size="sm">
            <Link to={`/projects/${projectId}/walkthroughs/${latest.id}`}>
              View live run
            </Link>
          </Button>
          {scenarioTo && (
            <Button asChild variant="ghost" size="sm">
              <Link to={scenarioTo}>View scenario</Link>
            </Button>
          )}
        </div>
      </PaperCard>
    </section>
  );
}

export function FailedWalkthroughHeroCard({
  projectId,
  latest,
  lastCompleted,
  scenarioTo,
}: WalkthroughHeroStatusCardProps) {
  return (
    <section>
      <DashboardSectionHeader
        kicker="Latest walkthrough"
        note="Execution failed"
        action={
          lastCompleted ? (
            <Link
              to={`/projects/${projectId}/walkthroughs/${lastCompleted.id}`}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              View last completed report →
            </Link>
          ) : undefined
        }
      />
      <PaperCard
        as="article"
        padding="md"
        className="relative overflow-hidden space-y-6 border-rose-500/30 bg-rose-500/[0.02]"
      >
        <WalkthroughPersonaHeader
          personaName={latest.personaName}
          personaTo={
            latest.personaId
              ? `/projects/${projectId}/personas/${latest.personaId}`
              : null
          }
          scenarioName={latest.scenarioName}
          scenarioTo={scenarioTo}
          timestamp={latest.completedAt ?? latest.createdAt}
        />

        <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-2.5 py-0.5 text-xs font-semibold text-rose-600 dark:text-rose-400">
              <AlertTriangle className="h-3 w-3 text-rose-600 dark:text-rose-400" />
              {latest.status === "timeout" ? "Timed out" : "Execution failed"}
            </span>
            <span className="text-xs text-muted-foreground">
              Walkthrough could not be completed
            </span>
          </div>
          <p className="text-xs font-mono text-foreground leading-relaxed break-words">
            {latest.errorMessage ||
              "Runner encountered an error or connection was aborted before observations were finished."}
          </p>
        </div>

        {lastCompleted && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/30 px-3.5 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span>Last successful run:</span>
              <strong className="font-medium text-foreground">
                {lastCompleted.scenarioName ?? "Scenario"}
              </strong>
              <span>·</span>
              <Timestamp
                date={lastCompleted.completedAt ?? lastCompleted.createdAt}
              />
            </div>
            <Link
              to={`/projects/${projectId}/walkthroughs/${lastCompleted.id}`}
              className="font-medium text-primary hover:underline"
            >
              View report →
            </Link>
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-t border-border/60 pt-4">
          <Button asChild size="sm" variant="destructive">
            <Link to={`/projects/${projectId}/walkthroughs/${latest.id}`}>
              Investigate failure
            </Link>
          </Button>
          {scenarioTo && (
            <Button asChild variant="ghost" size="sm">
              <Link to={scenarioTo}>View scenario</Link>
            </Button>
          )}
        </div>
      </PaperCard>
    </section>
  );
}
